import type {
  PeCohortBaseline,
  PeCohortDomainAverage,
  PeCompositeGrowth,
  PeContent,
  PeReport,
  PeSchool,
  PeSection,
  PeTrajectoryPoint,
} from '../ProgramEvaluation/types';

export const SAMPLE_DISTRICT_NAME = 'Cedar Valley Unified School District';

const YEARS = ['2021-22', '2022-23', '2023-24', '2024-25', '2025-26'];

function trajectory(levels: number[], scales: number[]): PeTrajectoryPoint[] {
  return YEARS.map((school_year, i) => ({
    school_year,
    average_scale_score: scales[i] ?? 0,
    average_proficiency_level: levels[i] ?? 0,
    reset_era: i === YEARS.length - 1 ? 'post_reset' : 'pre_reset',
  }));
}

function growth(levels: number[], scales: number[], delta: number, lastDelta: number): PeCompositeGrowth {
  const current = levels[levels.length - 1] ?? null;
  const previous = levels[levels.length - 2] ?? null;
  return {
    active_window: 'spring',
    active_window_description: 'Spring 2025 to Spring 2026',
    first_post_reset_year: true,
    district_average_scale_score_delta: Math.round(delta * 60),
    district_average_scale_score_delta_last_year: Math.round(lastDelta * 60),
    district_average_proficiency_level_delta: delta,
    district_average_proficiency_level_delta_last_year: lastDelta,
    current_average_proficiency_level: current,
    previous_average_proficiency_level: previous,
    trajectory_chart_data: trajectory(levels, scales),
  };
}

function school(
  school_name: string,
  school_type: string,
  el_count: number,
  newcomer_count: number,
  ltel_count: number,
  exited: number,
  growthDelta: number,
): PeSchool {
  return {
    school_name,
    school_type,
    el_count,
    current_member_count: el_count,
    total_enrollment: null,
    el_share_of_enrollment: null,
    newcomer_count,
    newcomer_share_of_school_els: Math.round((newcomer_count / el_count) * 100) / 100,
    composite_growth_scale_score_delta: Math.round(growthDelta * 60),
    composite_growth_proficiency_level_delta: growthDelta,
    composite_growth_last_year: growthDelta,
    reclassification_rate: Math.round((exited / el_count) * 100) / 100,
    ltel_count,
    ltel_share_of_school_els: Math.round((ltel_count / el_count) * 100) / 100,
    school_finding_eligible: true,
  };
}

function domainAverages(cohort: number[], others: number[]): PeCohortDomainAverage[] {
  return ['composite', 'listening', 'speaking', 'reading', 'writing'].map((domain, i) => ({
    domain,
    cohort_proficiency_level: cohort[i] ?? null,
    non_cohort_proficiency_level: others[i] ?? null,
    cohort_scale_score: Math.round((cohort[i] ?? 0) * 100),
    non_cohort_scale_score: Math.round((others[i] ?? 0) * 100),
  }));
}

function cohort(
  cohort_size: number,
  percent_of_program: number,
  year_over_year_change: number,
  delta: number,
  comparisonDelta: number,
  averages: PeCohortDomainAverage[],
  reclassified: number,
  extra: Partial<PeCohortBaseline> = {},
): PeCohortBaseline {
  return {
    cohort_size,
    percent_of_program,
    year_over_year_change,
    growth: {
      composite: {
        scale_score_delta: Math.round(delta * 60),
        scale_score_delta_last_year: Math.round(delta * 60),
        proficiency_level_delta: delta,
        proficiency_level_delta_last_year: delta,
      },
      by_domain: {},
    },
    reclassification_within_cohort: {
      reclassified_count: reclassified,
      eligible_count: cohort_size,
      rate: Math.round((reclassified / cohort_size) * 100) / 100,
    },
    comparison_to_non_cohort: {
      growth_composite_delta: Math.round(comparisonDelta * 60),
      growth_composite_proficiency_delta: comparisonDelta,
      growth_by_domain_delta: {},
      has_non_cohort_comparison: true,
    },
    small_n_caveat: false,
    domain_averages: averages,
    ...extra,
  };
}

const ENROLLMENT_YEARS = [
  { el: 470, newcomers: 70, ltel: 150, identified: 80, exited: 20 },
  { el: 482, newcomers: 72, ltel: 154, identified: 82, exited: 22 },
  { el: 476, newcomers: 68, ltel: 151, identified: 78, exited: 24 },
  { el: 489, newcomers: 76, ltel: 152, identified: 75, exited: 19 },
  { el: 548, newcomers: 74, ltel: 157, identified: 88, exited: 14 },
];

export const sampleReport: PeReport = {
  district: {
    name: SAMPLE_DISTRICT_NAME,
    school_year: '2025-26',
    el_student_count: 548,
    el_student_count_last_year: 489,
    school_count_with_els: 12,
    data_window: 'spring',
    monitored_student_count: 61,
    monitored_student_count_last_year: 52,
  },
  enrollment: {
    el_count_this_year: 548,
    el_count_last_year: 489,
    net_change: 59,
    new_identifications_this_year: 88,
    departures_this_year: { reclassified: 14, moved_out: 12, other: 3, total: 29 },
    newcomer_count: 74,
    newcomers_among_new_identifications: 74,
    ltel_count: 157,
    multi_year_enrollment: ENROLLMENT_YEARS.map((row, i) => ({
      school_year: YEARS[i] ?? '',
      el_count: row.el,
      newcomer_count: row.newcomers,
      ltel_count: row.ltel,
      newly_identified_count: row.identified,
      exited_count: row.exited,
    })),
  },
  growth: {
    composite: growth([2.2, 2.4, 2.5, 2.6, 2.8], [310, 318, 326, 334, 428], 0.4, 0.4),
    by_domain: {
      listening: growth([2.5, 2.7, 2.8, 2.9, 3.1], [318, 326, 334, 342, 440], 0.4, 0.4),
      speaking: growth([2.6, 2.8, 2.9, 3.0, 3.2], [322, 330, 338, 346, 446], 0.4, 0.4),
      reading: growth([2.1, 2.3, 2.4, 2.5, 2.7], [302, 310, 318, 326, 420], 0.4, 0.3),
      writing: growth([2.0, 2.2, 2.3, 2.4, 2.6], [296, 304, 312, 320, 412], 0.3, 0.3),
    },
    by_domain_notes: {
      largest_gain_domain: 'speaking',
      largest_gap_domain: 'writing',
      range_is_tight: false,
      range_scale_score_points: 28,
    },
  },
  flashlight: {
    is_integrated: true,
    scale_label: 'Flashlight rubric, 1 to 5',
    by_domain_overlay_data: {
      speaking: [
        {
          school_year: '2025-26',
          benchmark_windows: [
            { window: 'BOY', average_rubric_score: 2.7, n_students: 425 },
            { window: 'MOY', average_rubric_score: 2.9, n_students: 425 },
            { window: 'EOY', average_rubric_score: 3.0, n_students: 425 },
          ],
          domain_coverage: { students_with_any_submission_this_year: 425, el_student_total_this_year: 548 },
        },
      ],
      writing: [
        {
          school_year: '2025-26',
          benchmark_windows: [
            { window: 'BOY', average_rubric_score: 1.8, n_students: 425 },
            { window: 'MOY', average_rubric_score: 2.0, n_students: 425 },
            { window: 'EOY', average_rubric_score: 2.1, n_students: 425 },
          ],
          domain_coverage: { students_with_any_submission_this_year: 425, el_student_total_this_year: 548 },
        },
      ],
    },
  },
  reclassification: {
    this_year: { reclassified_count: 14, eligible_count: 14, rate: 0.03 },
    last_year: { reclassified_count: 19, eligible_count: 19, rate: 0.04 },
    median_years_in_program_for_reclassifiers: 5,
    near_reclassification_count: 26,
    near_reclassification_by_unmet_domain: { speaking: 26 },
    multi_year_rates: ENROLLMENT_YEARS.map((row, i) => ({
      school_year: YEARS[i] ?? '',
      rate: Math.round((row.exited / row.el) * 100) / 100,
      reclassified_count: row.exited,
      eligible_count: row.exited,
    })),
    pipeline_projection: {
      status: 'pending',
      placeholder_copy:
        'District-wide pipeline projections need a second post-reset ACCESS administration. They arrive with the Spring 2027 results.',
    },
  },
  ltel: {
    count: 157,
    percent_of_program: 0.29,
    count_last_year: 152,
    newly_entered_count: 14,
    district_grown_count: 143,
    prevention_metric: {
      at_risk_count: 64,
      subline_text: 'in year 4, one year before LTEL',
      years_range_description: 'in year 4 of the program',
      years_range_description_no_subject: 'year 4 of the program',
    },
    multi_year_counts: ENROLLMENT_YEARS.map((row, i) => ({
      school_year: YEARS[i] ?? '',
      count: row.ltel,
      newly_entered: [12, 13, 11, 12, 14][i] ?? null,
    })),
  },
  by_school: [
    school('Lakeshore Elementary', 'Elementary', 42, 10, 9, 3, 0.5),
    school('Meadowbrook Elementary', 'Elementary', 43, 7, 3, 1, 0.5),
    school('Pinecrest Elementary', 'Elementary', 52, 11, 3, 0, 0.6),
    school('Riverside Elementary', 'Elementary', 45, 9, 4, 0, 0.5),
    school('Sunset Elementary', 'Elementary', 33, 8, 5, 0, 0.4),
    school('Valley Ridge Elementary', 'Elementary', 34, 11, 3, 0, 0.5),
    school('Canyon Ridge Middle School', 'Middle', 45, 2, 21, 2, 0.4),
    school('Cedar Valley Middle School', 'Middle', 50, 2, 24, 2, 0.4),
    school('Northridge Middle School', 'Middle', 60, 2, 22, 5, 0.3),
    school('Cedar Valley High School', 'High', 62, 7, 26, 1, 0.3),
    school('Eastlake High School', 'High', 40, 3, 21, 0, 0.2),
    school('Summit High School', 'High', 42, 2, 16, 0, 0.2),
  ],
  subgroups: {
    ltel: cohort(157, 0.29, 5, 0.3, -0.1, domainAverages([3.4, 3.6, 3.7, 3.3, 3.1], [2.6, 2.9, 3.0, 2.5, 2.4]), 3, {
      newly_entered_count: 14,
      district_grown_count: 143,
      cohort_largest_growth_domain: 'speaking',
      cohort_smallest_growth_domain: 'writing',
      cohort_dominant_unmet_domain: 'speaking',
    }),
    newcomer: cohort(74, 0.14, -2, 0.5, 0.1, domainAverages([1.7, 1.9, 2.0, 1.6, 1.5], [3.0, 3.3, 3.4, 2.9, 2.8]), 0),
    near_reclassification: cohort(26, 0.05, 8, 0.3, -0.1, domainAverages([4.2, 4.4, 3.2, 4.3, 4.1], [2.7, 3.0, 3.2, 2.6, 2.5]), 0, {
      cohort_dominant_unmet_domain: 'speaking',
    }),
    dually_identified: cohort(57, 0.1, 14, 0.3, -0.1, domainAverages([2.6, 2.9, 3.0, 2.5, 2.4], [2.8, 3.1, 3.2, 2.7, 2.6]), 1),
    alternate_access: { absent: true },
  },
  candidate_findings: { school_findings: [], subgroup_patterns: [], patterns_worth_investigating: [] },
  cross_section_top_findings: [],
  data_quality: {
    students_missing_wida_history: 0,
    students_with_pre_reset_only: 0,
    students_with_single_administration: 41,
    wida_composite_coverage_pct: 0.98,
    flashlight_coverage_pct: 0.78,
    warnings: [],
    notes: [
      'Cedar Valley Unified School District is a fictional district. Every number in this report is sample data, shown so you can see what a Program Evaluation looks like.',
    ],
  },
  metadata: {
    generated_at: '2026-06-30T00:00:00Z',
    el_student_count_used: 548,
    reset_era_handling: 'split',
    district_config_snapshot: {
      State: 'UT',
      CompositeMinimum: 4.2,
      DomainMinimums: { speaking: 3.5 },
      LTELThreshold: 4,
      NewcomerMaxYears: 1,
      MonitoringYears: 4,
    },
  },
  cannot_generate: false,
  assessment_data_absent: false,
};

function section(element: string, text: string, cohort_key = ''): PeSection {
  return { element, cohort_key, text, source: 'sample' };
}

function narrative(element: string, text: string, cohort_key = ''): PeSection {
  return section(element, `<narrative>${text}</narrative>`, cohort_key);
}

function findings(element: string, items: string[]): PeSection {
  return section(element, items.map((item) => `<finding>${item}</finding>`).join(''));
}

function patterns(element: string, items: string[]): PeSection {
  return section(element, items.map((item) => `<pattern>${item}</pattern>`).join(''));
}

export const sampleContent: PeContent = {
  unit_mode: 'proficiency_level',
  sections: [
    narrative(
      'executive_summary_narrative',
      'Cedar Valley served 548 EL students this year across 12 schools, 59 more than last year. 14 students, 3% of active ELs, met Utah’s exit bar on this spring’s ACCESS; 12 of them are in grades 5 through 7. Every grade grew within 0.1 levels of the same grade last year. Inside grade 7, though, the 14 students who started the year at level 3 grew 0.1 levels against 0.3 last year, and 10 of them are at Northridge Middle.',
    ),
    findings('top_findings', [
      'Grade 7’s overall growth barely moved, but the 14 students who started at level 3 grew 0.1 levels against 0.3 last year. 10 of them are at Northridge Middle.',
      'Writing is the lowest domain in every grade, and sits 0.4 to 0.6 levels below Speaking from grade 2 on.',
      'Middle schools account for 9 of the 14 students who met Utah’s exit criteria this year.',
      'Long-term ELs are concentrated in grades 6 through 9, where 90 of the district’s 157 long-term ELs are enrolled.',
    ]),
    narrative(
      'enrollment_narrative',
      'EL enrollment grew by 59 students this year, to 548. 74 students are newcomers in their first year in U.S. schools. Grade 6 is the largest grade with 64 EL students; grades 1, 10, 11 and 12 are the smallest at 32 to 34.',
    ),
    narrative(
      'composite_growth_narrative',
      'Every grade grew within 0.1 levels of the same grade last year. Grades 2 and 12 rose by 0.1; grades 4 and 7 fell by 0.1. Grade 7’s overall growth dipped only slightly (0.2 against 0.3), but the 14 students who started at level 3 grew 0.1, against 0.3 for the same group last year.',
    ),
    narrative(
      'per_domain_growth_narrative',
      'In every grade the four domains sit in the same order: Speaking highest, then Listening, Reading, and Writing lowest. The gap between Speaking and Writing is 0.6 levels in kindergarten and grade 3, and 0.4 to 0.6 in most other grades. Flashlight Speaking and Writing each rose about 0.3 rubric points from fall to spring in grades 1 through 12.',
    ),
    narrative(
      'reclassification_narrative',
      '14 students met Utah’s exit bar on this spring’s ACCESS, 3% of active ELs. No student in kindergarten through grade 3 met it. The share rises from 3% of grade 4 to 10% of grade 7, then drops to one student in grade 9 and none in grades 10 through 12. 26 more students met the composite bar but fell short of the 3.5 Speaking minimum.',
    ),
    narrative(
      'ltel_narrative',
      '157 students, 29% of active ELs, have been in the program five or more years, up from 152 last year. The count peaks in grades 8 and 9 at 23 students each, then falls to 11 in grade 11 and 14 in grade 12.',
    ),
    findings('school_notable_findings', [
      '10 of the 14 grade 7 students who started at level 3 are at Northridge Middle. They grew 0.1 levels; last year the same grade and starting level there grew 0.3.',
      'At Eastlake High, 21 of 40 EL students are long-term ELs, the highest share in the district. Cedar Valley High holds the largest count, 26 students.',
    ]),
    narrative(
      'cohort_baseline',
      '157 students have been in the program five or more years, 29% of active ELs and 5 more than last year. 14 entered the cohort this year, 3 met the exit criteria, and 90 of the 157 are in grades 6 through 9.',
      'ltel',
    ),
    narrative(
      'cohort_baseline',
      '74 students are in their first year in U.S. schools, 14% of active ELs and 2 fewer than last year. 41 are kindergarteners; of the 33 in grades 1 through 12, 15 are in elementary grades.',
      'newcomer',
    ),
    narrative(
      'cohort_baseline',
      '26 students met the 4.2 composite bar but fell short of the 3.5 Speaking minimum, 8 more than last year. 19 are within 0.5 of the Speaking minimum, 11 are also long-term ELs, and 17 are in grades 6 through 8.',
      'near_reclassification',
    ),
    narrative(
      'cohort_baseline',
      '57 students carry both an EL and a special education designation, 10% of active ELs and 14 more than last year. 1 met the exit criteria, 24 are also long-term ELs, and 41 are in grades 3 through 9.',
      'dually_identified',
    ),
    patterns('subgroup_notable_patterns', [
      '41 of the 74 newcomers are kindergarteners, whose first year in school is also their first in U.S. schools. The newcomer count reads very differently with kindergarten in or out.',
      '26 students met the composite bar but not the 3.5 Speaking minimum, 8 more than last year. 17 of them are in grades 6 through 8.',
    ]),
    section('patterns_worth_investigating', ''),
  ],
};
